import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import type { RichContent, RicosEditorType } from '@wix/ricos';

import { selectGroupStatus } from 'store/selectors';
import { useDidUpdate } from 'common/hooks';
import { RicosEditor } from 'common/components/Ricos/loadable';
import { RceEditorFallback } from 'common/components/Ricos/RceFallback';
import { GROUPS_APP_DEFINITION_ID } from '../../../../constants';

import { CardContent } from 'wui/CardContent';
import { CardActions } from 'wui/CardActions';
import { Button } from 'wui/Button';
import { ButtonGroup } from 'wui/ButtonGroup';
import { InputDialog } from 'wui/InputDialog';
import { DialogMobileHeader } from 'wui/DialogMobileHeader';
import { DialogContent } from 'wui/DialogContent';
import { TextButton } from 'wui/TextButton';

interface IProps {
  isOpen?: boolean;
  groupId: string;
  content?: RichContent;

  onClose(): void;

  onSubmit(content?: RichContent): void;
}

export function CustomSourceEditor(props: IProps) {
  const { content, groupId, isOpen, onClose, onSubmit } = props;

  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const { applications } = useSelector(selectGroupStatus(groupId));

  const [isBusy, setIsBusy] = useState(false);

  const $ricos = useRef<RicosEditorType>(null);

  const isLoading = isBusy || applications.updating;

  useDidUpdate(() => {
    if (!applications.updating) {
      onClose();
    }
  }, [applications.updating]);

  if (!isMobile) {
    return (
      <>
        <CardContent disableSideGutters>
          <RicosEditor
            wired
            ref={$ricos}
            usage="CustomTab"
            postId={groupId}
            containerId={GROUPS_APP_DEFINITION_ID}
            contentId="custom_tab"
            content={content}
            groupId={groupId}
            onBusyChange={handleEditorBusyChange}
            fallback={<RceEditorFallback wired />}
            loadingPlaceholder={<RceEditorFallback wired />}
          />
        </CardContent>
        <CardActions align="right">
          <ButtonGroup>
            <Button outlined onClick={onClose}>
              {t('groups-web.cancel')}
            </Button>
            <Button
              loading={isLoading}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {t('groups-web.save')}
            </Button>
          </ButtonGroup>
        </CardActions>
      </>
    );
  }

  return (
    <InputDialog
      isOpen={isOpen}
      hideCloseButton
      focusTrap={false}
      onClose={onClose}
      resize="native"
      scalable={false}
    >
      <DialogMobileHeader>
        <TextButton onClick={onClose} variant="secondary">
          {t('groups-web.cancel')}
        </TextButton>
        <TextButton
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {t('groups-web.save')}
        </TextButton>
      </DialogMobileHeader>

      <DialogContent scrollable={false} disableSideGutters>
        <RicosEditor
          ref={$ricos}
          usage="CustomTab"
          postId={groupId}
          containerId={GROUPS_APP_DEFINITION_ID}
          contentId="custom_tab"
          content={content}
          groupId={groupId}
          onBusyChange={handleEditorBusyChange}
          fallback={<RceEditorFallback wired />}
          loadingPlaceholder={<RceEditorFallback wired />}
        />
      </DialogContent>
    </InputDialog>
  );

  function handleEditorBusyChange(isBusy: boolean) {
    setIsBusy(isBusy);
  }

  async function handleSubmit() {
    setIsBusy(true);
    const content = await $ricos.current?.prePublish();
    const { isEmpty } = $ricos.current?.getContentTraits() || {};

    if (isEmpty) {
      onSubmit();
    } else {
      onSubmit(content);
    }
    setIsBusy(false);
  }
}

CustomSourceEditor.displayName = 'CustomSourceEditor';
